// Optional drop-in sound files (art-assets §8): an `assets/sounds/<file>.ogg` (or `.mp3`) listed
// in the asset manifest replaces the synthesised sound of its slot. OGG wins over MP3, as in the
// server's manifest scan. The bytes are fetched as `/assets/sounds/<file>.<ext>?v=<hash>` and
// handed to the engine, which decodes them once its context exists (after the unlock gesture)
// and scales them from their −3 dBFS normalisation to the slot's loudness (audio §4.1).
//
// The manifest carries the audio entries: the server lists them (`w = h = 0`) and the client's
// `parseManifest` keeps them (assets/manifest.ts, MANIFEST_FILE_RE accepts `.ogg|.mp3`).
//
// Implementer: client-audio (lane A). Consumer: app.ts (after the manifest fetch).

import { serverUrl } from '../net/server-url.js';
import type { AudioEngine } from './engine.js';
import type { SoundSlot } from './recipes.js';

/** Slot → file name under `assets/sounds/` (no extension; art-assets §1: a–z, 0–9 and `-`). */
export const DROP_IN_FILES: Readonly<Record<SoundSlot, string>> = Object.freeze({
  click: 'click',
  split: 'split',
  shoot: 'shoot',
  eat: 'eat',
  merge: 'merge',
  pop: 'pop',
  death: 'death',
  cashStart: 'cash-start',
  cashTick: 'cash-tick',
  cashOut: 'cash-out',
  cashCancel: 'cash-cancel',
  moneyGain: 'money-gain',
  notice: 'notice',
  error: 'error',
  countdown: 'countdown',
  countdownEnd: 'countdown-end',
  zone: 'zone',
  toggle: 'toggle',
  levelUp: 'level-up',
  capture: 'capture',
  captureMid: 'capture-mid',
  captureBig: 'capture-big',
  kill: 'kill',
  deathArena: 'death-arena',
  shatter: 'shatter',
  spill: 'spill',
  collect: 'collect',
  homeAlarm: 'home-alarm',
  reveal: 'reveal',
});

/** Drop-in files are at most this big (art-assets §8: ≤ 100 KB; a little slack). */
export const DROP_IN_MAX_BYTES = 256 * 1024;

export interface DropInFile {
  readonly slot: SoundSlot;
  /** Path under /assets/, e.g. `sounds/eat.ogg`. */
  readonly path: string;
  readonly hash: string;
}

/** The drop-in files present in a manifest's `files` (OGG preferred over MP3). */
export function dropInFilesFor(
  files: Readonly<Record<string, { readonly hash: string }>>,
): DropInFile[] {
  const out: DropInFile[] = [];
  for (const slot of Object.keys(DROP_IN_FILES) as SoundSlot[]) {
    const base = `sounds/${DROP_IN_FILES[slot]}`;
    for (const ext of ['ogg', 'mp3']) {
      const path = `${base}.${ext}`;
      const f = Object.prototype.hasOwnProperty.call(files, path) ? files[path] : undefined;
      if (f && typeof f.hash === 'string') {
        out.push({ slot, path, hash: f.hash });
        break;
      }
    }
  }
  return out;
}

export interface LoadDropInsOptions {
  /** Server base (net/server-url.ts); `""` = this page. */
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

/**
 * Fetches every drop-in file listed in `files` and hands it to `engine`. Never rejects; returns
 * how many files were handed over (decoding happens later, in the engine).
 */
export async function loadDropIns(
  engine: Pick<AudioEngine, 'setDropInData'>,
  files: Readonly<Record<string, { readonly hash: string }>>,
  opts: LoadDropInsOptions = {},
): Promise<number> {
  const fetchImpl = opts.fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
  if (!fetchImpl) return 0;
  const list = dropInFilesFor(files);
  const results = await Promise.all(
    list.map(async (f) => {
      try {
        const res = await fetchImpl(serverUrl(opts.baseUrl ?? '', `/assets/${f.path}?v=${f.hash}`));
        if (!res.ok) return false;
        const data = await res.arrayBuffer();
        if (data.byteLength === 0 || data.byteLength > DROP_IN_MAX_BYTES) return false;
        engine.setDropInData(f.slot, data);
        return true;
      } catch {
        return false;
      }
    }),
  );
  return results.filter(Boolean).length;
}
